/**
 * Persists the multi-step patient upload flow (Prescriptions → Reports → …) in sessionStorage
 * so a refresh does not drop React Router `location.state` and accidentally show *all* account
 * documents instead of only this flow's uploads.
 */
const STORAGE_KEY = "healbid:patientUploadFlowV1";

export function readPatientUploadFlowState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Merges saved session with current navigation state. `locationState` wins (explicit navigation).
 */
export function mergePatientUploadFlowState(locationState) {
  const stored = readPatientUploadFlowState();
  const nav = locationState && typeof locationState === "object" ? locationState : {};
  return { ...stored, ...nav };
}

export function persistPatientUploadFlowState(partial) {
  try {
    if (!partial || typeof partial !== "object") return;
    const next = { ...readPatientUploadFlowState(), ...partial };
    if (Object.keys(next).length === 0) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearPatientUploadFlowState() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Whether the multi-step upload UI should only list documents for the current
 * flow (`uploadedDocIds`), not every prescription on the account.
 * Defaults to scoped: only an explicit `startFlow: false` opts into the legacy
 * "show all documents" mode (e.g. direct /Prescriptions with no flow state
 * was wrongly treated as unscoped and showed files from past requests).
 */
export function isPatientUploadFlowScoped(flowState) {
  if (!flowState || typeof flowState !== "object") return true;
  return flowState.startFlow !== false;
}

/** Care request from linear flow (`/request/:id/...`); when set, uploads and GET /app/prescriptions are scoped. */
export function getLinearMatchRequestId(flowState) {
  if (!flowState || typeof flowState !== "object") return null;
  const id = flowState.linearMatchRequestId;
  if (id == null || id === "") return null;
  return String(id);
}
